import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useTransition, useChain, animated, useSpringRef } from '@react-spring/web';
import { CLINICAL_PATHS, isClinicalAccountUser } from '../../../helpers/clinical_account';

import MobileNavItem from '../header/mobile_nav_item';
import PortalNav from '../header/portal_nav';
import Context from '../../../utils/context';

export default function ProviderHeader({}) {
  const context = useContext(Context);
  const isMobile = context.viewport.getIsMobile();
  const [navOpen, setNavOpen] = useState(false);

  let location = useLocation();

  const [headerSticky, setHeaderSticky] = useState(false);

  useEffect(() => {
    if (location.pathname.includes('surgeon/dr-')) {
      setHeaderSticky(true);
    } else {
      setHeaderSticky(false);
    }
  });

  const hideProviderAccountDetails = isClinicalAccountUser(context.auth.getCurrentUser());

  const menuItems = [
    {
      title: 'Give feedback',
      smallTitle: 'Help us improve and make your life easier!',
      props: {
        to: '/contact-us',
      },
      pendoTag: 'Surgeons CRM nav - give feedback',
    },
    ...(!hideProviderAccountDetails
      ? [
          {
            title: 'Account details',
            hr_top: true,
            props: {
              to: '/provider/account-details',
            },
            pendoTag: 'Surgeons CRM nav - account details',
          },
        ]
      : []),
    {
      link: 'mailto:support@patientpartner.com',
      title: 'Help & support',
      hr_top: hideProviderAccountDetails,
      pendoTag: 'Surgeons CRM nav - help and support',
    },
    {
      onclick: 'logout',
      title: 'Log out',
      pendoTag: 'Surgeons CRM nav - log out',
    },
  ];

  const mobileMenuItems = context.auth.isLoggedIn()
    ? [
        {
          mainLink: {
            link: '/contact-us',
            title: 'Give feedback',
            smallTitle: 'Help us improve and make your life easier!',
            pendoTag: 'Surgeons CRM nav - contact us',
          },
        },
        ...(!hideProviderAccountDetails
          ? [
              {
                mainLink: {
                  link: '/provider/account-details',
                  title: 'Account details',
                  hr_top: true,
                  pendoTag: 'Surgeons CRM nav - account details',
                },
              },
            ]
          : []),
        {
          mainLink: {
            link: 'mailto:support@patientpartner.com',
            title: 'Help & support',
            hr_top: hideProviderAccountDetails,
            pendoTag: 'Surgeons CRM nav - help',
          },
        },
        {
          mainLink: {
            onclick: 'logout',
            title: 'Log out',
            pendoTag: 'Surgeons CRM nav - log out',
          },
        },
      ]
    : [
        {
          mainLink: {
            title: 'Join for free',
            link: '/member/join/pre-signup',
          },
        },
        {
          mainLink: {
            title: 'Log in',
            link: '/member/login',
          },
        },
      ];

  const navItemsAnimation = useSpringRef();
  const mobileNavItems = useTransition(navOpen ? [...mobileMenuItems] : [], {
    ref: navItemsAnimation,
    trail: navOpen ? 100 / mobileMenuItems.length : 0,
    config: { mass: 0.5, tension: 200, friction: 20 },
    from: { opacity: 0, transform: 'translateX(50px)' },
    enter: { opacity: 1, transform: 'translateX(0px)' },
  });

  useChain(navOpen ? [navItemsAnimation] : [navItemsAnimation], [0.2]);

  return (
    <header className={`${headerSticky === true ? 'nosticky' : ''} platform-header`} id='hdr-content'>
      <div className='container-fluid'>
        <div className='header-outr row'>
          <div className={`${isMobile ? 'col-4' : 'col-2'}`}>
            <div className='hdr-logo'>
              <Link to={hideProviderAccountDetails ? CLINICAL_PATHS.programInsights : '/'}>
                <img
                  alt='logo'
                  id='logo'
                  src='https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/logos/svg/pp-logo.svg'
                />
              </Link>
            </div>
          </div>
          <div className={`text-center ${isMobile ? '' : 'col'}`}>
            <div className='header-innr'>
              <div className='navbar-header'>
                <nav
                  className='navbar navbar-expand-md navbar-light sidenav'
                  id='mySidenav'
                  style={{ right: navOpen ? '0px' : '-100%' }}
                >
                  {isMobile === true && (
                    <Link
                      onClick={() => setNavOpen(false)}
                      to={hideProviderAccountDetails ? CLINICAL_PATHS.programInsights : '/'}
                    >
                      <img
                        alt='logo'
                        className={`mobile-nav-logo ${navOpen ? 'active' : ''}`}
                        src='https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/logos/svg/pp-logo.svg'
                      />
                    </Link>
                  )}
                  <div>
                    <ul className='navbar-nav'>
                      {isMobile && (
                        <>
                          {mobileNavItems((style, menuItem) => (
                            <animated.div
                              key={menuItem.mainLink.title}
                              style={{ ...style }}
                              className='mobile-nav-item-animated-wrap'
                            >
                              <MobileNavItem
                                navItem={menuItem}
                                setNavOpen={setNavOpen}
                                pendoTag={menuItem.mainLink.pendoTag}
                              />
                            </animated.div>
                          ))}
                        </>
                      )}
                    </ul>
                  </div>
                </nav>
              </div>
            </div>
          </div>
          <div className={`hdr-btn-wrap ${isMobile ? 'col-7' : 'col-3'}`}>
            <div style={{ width: '100%' }} className='hdr-btn-innr in-platform'>
              {!isMobile && (
                <>
                  <PortalNav navigationItems={menuItems} />
                </>
              )}
            </div>
          </div>
          <div
            style={isMobile ? {} : { width: 'max-content', maxWidth: 'max-content' }}
            className={`${isMobile ? 'col-1' : ''}`}
          >
            <svg
              className={`tgl-innr ${navOpen ? 'active' : ''}`}
              onClick={() => {
                setNavOpen(!navOpen);
              }}
              width='30'
              height='31'
              viewBox='0 0 30 31'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M15 0.482178C6.71371 0.482178 0 7.17191 0 15.4286C0 23.6853 6.71371 30.375 15 30.375C23.2863 30.375 30 23.6853 30 15.4286C30 7.17191 23.2863 0.482178 15 0.482178ZM22.7419 25.8911C20.5706 27.4882 17.8972 28.4465 15 28.4465C12.1028 28.4465 9.42944 27.4882 7.25806 25.8911V25.0715C7.25806 22.944 8.99395 21.2143 11.129 21.2143C11.8004 21.2143 12.7923 21.9014 15 21.9014C17.2137 21.9014 18.1935 21.2143 18.871 21.2143C21.006 21.2143 22.7419 22.944 22.7419 25.0715V25.8911ZM24.5927 24.2337C24.1815 21.4373 21.7923 19.2857 18.871 19.2857C17.631 19.2857 17.0323 19.9728 15 19.9728C12.9677 19.9728 12.375 19.2857 11.129 19.2857C8.20766 19.2857 5.81855 21.4373 5.40726 24.2337C3.26008 21.9134 1.93548 18.8277 1.93548 15.4286C1.93548 8.2507 7.79637 2.41075 15 2.41075C22.2036 2.41075 28.0645 8.2507 28.0645 15.4286C28.0645 18.8277 26.7399 21.9134 24.5927 24.2337ZM15 7.23218C12.0605 7.23218 9.67742 9.60673 9.67742 12.5357C9.67742 15.4648 12.0605 17.8393 15 17.8393C17.9395 17.8393 20.3226 15.4648 20.3226 12.5357C20.3226 9.60673 17.9395 7.23218 15 7.23218ZM15 15.9107C13.131 15.9107 11.6129 14.398 11.6129 12.5357C11.6129 10.6735 13.131 9.16075 15 9.16075C16.869 9.16075 18.3871 10.6735 18.3871 12.5357C18.3871 14.398 16.869 15.9107 15 15.9107Z'
                fill='#314D69'
              ></path>
            </svg>
          </div>
        </div>
      </div>
    </header>
  );
}
